import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';

import {
  CART_ACTIONS,
  ORDER_ACTIONS,
  ORDER_CONFIRM_ACTIONS,
  PREORDER_ACTIONS,
} from '../../store/actions';
import { Status } from '../../store/models/europrisme';
import { Colors, Sizing } from '../../styles/vars';
import appConfig from '../../utils/app-config';
import { connect } from '../../utils/redux';
import Paths from '../routes/paths';
import Button from './Button';
import CartList from './CartList';
import Input from './Input';
import { getMessage } from './ProductDelay';
import ProductPrice from './ProductPrice';

const CartQuoteCheckoutWrapper = styled.form`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  grid-column-start: 9;
  grid-column-end: span 4;
  margin-bottom: 25px;
  padding: 30px;

  color: ${Colors.white};
  background-color: ${Colors.darkGrey};

  @media print {
    flex-direction: row-reverse;
    align-items: center;
    grid-column-start: 1;
    grid-column-end: span 12;
    padding: 8px;
    margin-top: 0;

    color: ${Colors.black};
    background: ${Colors.white};
    border: 1px solid #ddd;
  }

  h4 {
    margin: 0;
    margin-bottom: 30px;

    color: inherit;
    font-size: 20px;
    line-height: 24px;
    font-weight: bold;

    @media print {
      display: none;
    }
  }

  .line {
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    width: 100%;

    &:not(:last-of-type) {
      margin-bottom: 20px;
    }

    @media print {
      justify-content: flex-end;
      margin-bottom: 0 !important;
    }

    .header,
    .product-price {
      display: block;
      flex-basis: 0;
      flex-grow: 1;

      @media print {
        flex-basis: unset;
        flex-grow: unset;
        width: unset;
      }
    }

    .header {
      margin-right: ${Sizing.gutterWidth};
      text-align: end;

      @media print {
        margin-right: 20px;
      }

      span {
        font-size: 20px;
        line-height: 24px;
        font-weight: 500;

        &.small {
          margin-left: 1ch;
          font-size: 10px;
          line-height: 12px;
        }

        @media print {
          font-size: 16px;
          font-weight: normal;
        }
      }
    }
  }

  .longest-delay {
    margin-bottom: 30px;

    @media print {
      margin-bottom: 0;
      flex-grow: 1;

      font-size: 14px;
      white-space: nowrap;
    }
  }
`;

const OrderComment = styled.div`
  display: block;
  position: relative;
  width: 100%;
  padding: 30px;

  color: ${Colors.white};
  background-color: ${Colors.lightGrey};
  border-radius: 20px;

  text-align: left;
  white-space: pre-wrap;
  font-size: 16px;
  line-height: 19px;
  font-weight: 500;

  @media print {
    margin: 20px 0;
    padding: 15px;

    border-radius: 10px;

    font-size: 14px;
    font-weight: normal;

    &::before {
      content: 'Commentaire de commande :';
      position: absolute;
      bottom: calc(100% + 5px);
      left: 15px;

      color: black;
    }
  }
`;

const InvalidProducts = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  grid-column-start: 9;
  grid-column-end: span 4;
  margin-bottom: 40px;
  padding: 10px 16px;

  background-color: white;
`;

const CartQuoteCheckout = ({
  total,
  priceMode,
  isLoading,
  longestDelay,
  invalidProducts,
  isStatic,

  orderState,
  orderConfirmState,

  setOrderComment,
  preordersState,
  getPreorders,
  usersState,
}) => {
  const history = useHistory();

  const { currentUser } = usersState || {};
  const { comment, status } =
    (isStatic ? orderConfirmState.orderData : orderState) || {};

  const orderStateLoading = status === Status.LOADING;

  const cleanComment = (comment || '').replace(/<br\s*\/?>/, '');

  useEffect(() => {
    if (
      !preordersState ||
      (preordersState.status === Status.IDLE &&
        !preordersState.loaded &&
        Object.keys(preordersState.preorders || {}).length === 0)
    ) {
      getPreorders();
    }
  }, [preordersState]);

  const onSubmit = event => {
    event.preventDefault();

    const form = event.currentTarget;

    if (form && form.tagName === 'FORM') {
      const fd = new FormData(form);
      const sOrderComment = fd.get('sOrderComment');

      history.push(
        Paths.QuoteGenerator(null, {
          searchParams: { sOrderComment: btoa(sOrderComment) },
        }),
      );
    }
  };

  const shouldShowCheckout =
    !isStatic && currentUser && !currentUser.isInternal;

  const shouldShowPrice = !(currentUser && currentUser.isPriceHidden);

  return (
    <>
      <CartQuoteCheckoutWrapper
        method="GET"
        action={Paths.QuoteGenerator()}
        onSubmit={onSubmit}
        isStatic={!shouldShowCheckout}
      >
        <h4>Récapitulatif</h4>

        {shouldShowPrice && (
          <div className="line">
            <div className="header">
              <span>Total</span>
              <span className="small">({priceMode})</span>
            </div>

            {isLoading ? (
              <span className="product-price placeholder">Chargement...</span>
            ) : (
              <ProductPrice value={total} size={20} align="left" />
            )}
          </div>
        )}

        {appConfig.get('settings.useProductDelay') && (
          <div className="longest-delay">
            {isLoading ? <span>Chargement...</span> : getMessage(longestDelay)}
          </div>
        )}

        {shouldShowCheckout && (
          <>
            <Input
              type="text"
              id="sOrderComment"
              name="sOrderComment"
              placeholder="Coordonnées du client / commentaire"
              color={Colors.white}
              background={Colors.lightGrey}
              textarea
              value={cleanComment}
              onChange={setOrderComment}
            />

            <Button
              type="submit"
              color={Colors.white}
              background={Colors.primary}
              style={{
                fontSize: '15px',
                lineHeight: '20px',
                fontWeight: 500,
                marginTop: '20px',
              }}
              disabled={orderStateLoading}
            >
              Générer le devis
            </Button>
          </>
        )}

        {!shouldShowCheckout && comment && (
          <OrderComment>{comment}</OrderComment>
        )}
      </CartQuoteCheckoutWrapper>

      {invalidProducts.length > 0 && (
        <InvalidProducts>
          <h3
            style={{
              marginBottom: 0,
              marginTop: 0,
              width: '100%',
              fontWeight: 'normal',
            }}
          >
            Articles non-référencés
          </h3>

          <h5 style={{ marginTop: '6px', fontWeight: 'normal', opacity: 0.75 }}>
            Cette liste contient des produits inconnus à l&apos;application.
            <br />
            Une commande séparée sera passée, et nous vous contacterons afin de
            déterminer la meilleure façon de satisfaire votre commande.
          </h5>

          <CartList products={invalidProducts} invalid />
        </InvalidProducts>
      )}
    </>
  );
};

CartQuoteCheckout.propTypes = {
  total: PropTypes.number,
  priceMode: PropTypes.oneOf(['HT', 'TTC']),
  isLoading: PropTypes.bool,
  longestDelay: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.shape({ numeric: PropTypes.number }),
  ]),
  invalidProducts: PropTypes.arrayOf(PropTypes.objectOf(PropTypes.any)),
  isStatic: PropTypes.bool,

  usersState: PropTypes.objectOf(PropTypes.any),
  orderState: PropTypes.objectOf(PropTypes.any),
  orderConfirmState: PropTypes.objectOf(PropTypes.any),

  setOrderComment: PropTypes.func.isRequired,

  preordersState: PropTypes.objectOf(PropTypes.any),
  getPreorders: PropTypes.func.isRequired,
};

CartQuoteCheckout.defaultProps = {
  total: 0,
  priceMode: 'HT',
  isLoading: false,
  longestDelay: 0,
  invalidProducts: [],
  isStatic: false,

  usersState: {},
  orderState: {},
  orderConfirmState: {},
  preordersState: {},
};

export default connect(
  state => ({
    ...state.cart,
    usersState: state.users,
    cartState: state.cart.present,
    orderState: state.order,
    orderConfirmState: state.orderConfirm,
    preordersState: state.preorders,
  }),
  {
    ...CART_ACTIONS,
    ...ORDER_CONFIRM_ACTIONS,
    ...ORDER_ACTIONS,
    ...PREORDER_ACTIONS,
  },
)(CartQuoteCheckout);
